import { defineComponent, h, withDirectives, resolveDirective } from 'vue';
import { QSelect } from 'quasar';
import BaseComponent from './baseFormMixin.jsx';
import variableBindingMixin from './variableBindingMixin.jsx';
import utils from '../../../Shared/utils.jsx';
import methods from '../../../Shared/methods';
import careHelpfulFunctions from '../../careHelpfulFunctions.jsx';
import { appSettings } from '@/Shared/appSettings.js';
import { WithProxy } from '@/Shared/helperClasses';

import TranslationContainer from '../TranslationContainer.jsx';

export default defineComponent({
    name: 'sform-select',
    mixins: [BaseComponent, variableBindingMixin],
    components: {
        QSelect,
        TranslationContainer
    },
    data: () => ({
        titleMap: null,
        formatData: null,

        dolookupexpn: null,
        lookupurlexpn: null,
        lookuprawexpn: null,

        displayexpnfunc: null,
        valueexpnfunc: null,

        item_text: 'name',
        item_value: 'value',
        item_disabled: undefined,
        lookupitems: null,

        arrayfield: null,
        arrayelement: null,
        keyextractor: null,

        filter: '',
        customInputText: '',
        isCustomInputEditing: false,
        customInputDirty: false,
    }),
    props: {
    },
    created() {
        if (this.element.formatData)
            this.formatData = this.element.formatData;
        else if (this.element.schema && this.element.schema.formatData)
            this.formatData = this.element.schema.formatData;

        if (this.formatData && (this.formatData.LookupType || this.formatData.LookupURL || this.formatData.LookupInterpolatedExpression)) {
            if (this.formatData.LookupExpression)
                this.dolookupexpn = utils.compileExpression(this, this.formatData.LookupExpression);

            if ((!this.formatData.LookupType || this.formatData.LookupType == 'URL') && this.formatData.LookupURL)
                this.lookupurlexpn = utils.compile(this, this.formatData.LookupURL);
            else if (this.formatData.LookupType == 'Interpolated' && this.formatData.LookupInterpolatedExpression)
                this.lookuprawexpn = utils.compileExpression(this, this.formatData.LookupInterpolatedExpression);

            if (this.formatData.DisplayType == 'Expression') {
                const expr = utils.parseExpressionWithFilters(this.formatData.DisplayExpression);
                this.displayexpnfunc = {
                    code: expr,
                    func: new Function('control', 'util', this.formatData.LookupModelAs, `with (control) return ${expr};`)
                };
            }
            if (this.formatData.ValueType == 'Expression') {
                const expr = utils.parseExpressionWithFilters(this.formatData.ValueExpression);
                this.valueexpnfunc = {
                    code: expr,
                    func: new Function('control', 'util', this.formatData.LookupModelAs, `with (control) return ${expr};`)
                };
            }

            // Use our proxy for the context to prevent Vue3 complaining about unknown fields
            const ctx = new WithProxy(this).proxy;

            switch (this.formatData.DisplayType) {
                case 'Fieldname':
                    // Blank placeholder rows cannot carry their own label when DisplayField and
                    // ValueField name the same property. Resolve labels here instead.
                    this.item_text = (data) => {
                        if (data && data.$emptyrow) return this.emptyItemLabel();
                        return data ? this.getByPath(data, this.formatData.DisplayField) : undefined;
                    };
                    break;
                case 'Expression':
                    this.item_text = (data) => {
                        try {
                            if (data && data.$emptyrow) return this.emptyItemLabel();
                            return this.displayexpnfunc.func(ctx, careHelpfulFunctions, data);
                        }
                        catch (e) {
                            utils.warn(`LookupList DisplayType Expression failed to evaluate: ${this.displayexpnfunc.code}; original: ${this.formatData.DisplayExpression}`, e);
                            return '';
                        }
                    };
                    break;
            }
            switch (this.formatData.ValueType) {
                case 'Fieldname':
                    this.item_value = (data) => {
                        if (data && data.$emptyrow) return null;
                        return data ? this.getByPath(data, this.formatData.ValueField) : undefined;
                    };
                    break;
                case 'Expression':
                    this.item_value = (data) => {
                        try {
                            return data.$emptyrow ? null : this.valueexpnfunc.func(ctx, careHelpfulFunctions, data);
                        }
                        catch (e) {
                            utils.warn(`LookupList ValueType Expression failed to evaluate: ${this.valueexpnfunc.code}; original: ${this.formatData.ValueExpression}`, e);
                            return '';
                        }
                    };
                    break;
            }
        }
        else if (this.element.enum && !this.element.titleMap)
            this.titleMap = this.element.enum.map(i => ({ name: i, value: i }));
        else
            this.titleMap = this.element.titleMap.map(i => ({ name: i.name, value: i.value }));

        if (this.formatData && this.formatData.UniqueValues) {
            // If this item is marked for uniqueness, then we must locate the array where
            // the list of items are held. Then as we populate the items for the dropdown,
            // we can filter out items that have already been selected in another list.
            this.arrayfield = this.ParentByType('FormField_ArrayItem')?.Parent;
            if (this.arrayfield) {
                // Find the model reference that will allow us to read the array element from all
                // other array items.
                const keys = [...this.element.key];  // Ex: Queues.[].QueueName
                const arraykeys = this.ParentByType('FormField_ArrayItem').element.key; // Ex: Queues.[]
                keys.splice(0, arraykeys.length); // Ex: QueueName - isolate the element from the array down

                // This is sufficient for 99% of the use cases, but if the key happens to be in an array
                // for example Queues.[].Another.[1].QueueName, then this will fail. But I don't expect this
                // to ever be a real issue, so the effort to support it isn't worth it. (7/11/2021)
                this.keyextractor = new Function('item', `return item${keys.length ? `.${keys.join('.')}` : ''};`);

                this.arrayelement = this.arrayfield.FieldValue;
                if (this.arrayelement) { 
                    if (!this.arrayfield.chosenitems) this.arrayfield.chosenitems = {};
                    if (!this.arrayfield.availableitems) this.arrayfield.availableitems = {};
                    this.arrayfield.chosenitems[this.chosenitems_key] = Object.fromEntries(this.arrayelement.map(a => [this.keyextractor(a), true]));
                    this.item_disabled = (item) =>  this.getItemValue(item) != this.itemvalue && this.getItemValue(item) in this.arrayfield.chosenitems[this.chosenitems_key];
                }
            }
        }
    },
    unmounted() {
        if (this.arrayfield && this.arrayelement) {
            // Uniqueness has been requested and we have a reference to the array of items already picked.
            // We need to generate a lookup object containing every selected item so that we can disable
            // those that are already picked in the current dropdown list.
            if (!this.arrayfield.chosenitems) this.arrayfield.chosenitems = {};
            this.arrayfield.chosenitems[this.chosenitems_key] = Object.fromEntries(this.arrayelement.map(a => [this.keyextractor(a), true]));

            const items = this.lookupitems || this.titleMap;
            // Update the array container to control if the Add button is enabled or not
            this.arrayfield.availableitems[this.chosenitems_key] = items && (this.arrayelement.length < items.length);
        }
    },
    async mounted() {
        await this.Refresh();
        this.loadComplete();
    },
    computed: {
        chosenitems_key: function () {
            // Generate a key that is made from the name. Ex: Queue.[0].Name ==> Queue__0__Name
            return this.name.replace(/\./g, '_').replace(/\[/g, '_').replace(/\]/g, '_');
        },
        tolookup: function () {
            if (this.dolookupexpn)
                return utils.evaluate(this.dolookupexpn, this);
            else
                return true;
        },
        lookupurl: function () {
            if (this.lookupurlexpn)
                try {
                    return utils.evaluate(this.lookupurlexpn, this, false, null, true);
                }
                catch (e) {
                    utils.warn(`LookupList lookupurl could not be evaluated: ${this.lookupurlexpn.code}`, e);
                    return '';
                }
            else
                return '';
        },
        lookupraw: function () {
            if (this.lookuprawexpn)
                try {
                    return utils.evaluate(this.lookuprawexpn, this, false, null, true);
                }
                catch (e) {
                    utils.warn(`LookupList lookupraw could not be evaluated: ${this.lookuprawexpn.code}`, e);
                    return [];
                }
            else
                return '';
        },
        OnChangeData: function () {
            // This computed property is mixed into the Input data for a schema OnChange event action (see baseFormMixin.jsx)
            const items = this.lookupitems || this.titleMap || [];
            return {
                TitleMap: items.map(i => ({
                    value: this.getItemValue(i),
                    record: i,
                }))
            };
        },
        TitleMap: function () {
            const items = this.lookupitems || this.titleMap || [];
            return items.map(i => ({
                value: this.getItemValue(i),
                record: i,
            }));
        },
        listitems: function () {
            let items = this.lookupitems || this.titleMap || [];
            if (this.element?.schema?.$allows_null) {
                items = [...items, this.buildEmptyLookupRow()];
            }
            return items.map((item) => {
                const rawLabel = typeof this.item_text === 'function' ? this.item_text(item) : this.getByPath(item, this.item_text);
                const label = (rawLabel !== null && typeof rawLabel === 'object')
                    ? (rawLabel?.label ?? rawLabel?.text ?? rawLabel?.name ?? JSON.stringify(rawLabel))
                    : String(rawLabel ?? '');

                const value = this.getItemValue(item);
                return { ...item, _label: label, _value: value };
            });
        },
        selectListItems() {
            const items = this.listitems;
            let result = items;
            if (this.filter && (this.formatData?.AutoCompleteInput || this.formatData?.AllowCustomInput))
                result = items.filter(a => a._label.toLowerCase().includes(this.filter.toLowerCase()));

            // A field allowing custom input can hold a value that is not one of the offered options,
            // either because someone typed their own or because the list is filtered. The label shown
            // in the input is looked up from this list, so a value that is missing from it would
            // render as nothing. Keep the current value present, labelled as itself.
            if (this.formatData?.AllowCustomInput) {
                const value = this.itemvalue;
                if (value !== null && value !== undefined && value !== ''
                    && !result.some(a => a._value == value))
                    result = result.concat([{ _label: value, _value: value }]);
            }

            return result;
        },
        displayModelValue() {
            const value = this.itemvalue;
            if (this.formatData?.AllowCustomInput || this.valueExistsInOptions(value))
                return value;
            return null;
        },
    },
    watch: {
        lookupurl: function () {
            this.Refresh();
        },
        lookupraw: function () {
            this.Refresh();
        },
    },
    methods: {
        getByPath(obj, path) {
            if (obj == null || path == null || typeof path !== 'string') return undefined;
            if (!path.includes('.')) return obj[path];
            const parts = path.split('.');
            let v = obj;
            for (let i = 0; i < parts.length && v != null; i++)
                v = v[parts[i]];
            return v;
        },
        valueExistsInOptions(value) {
            if (value === null || value === undefined || value === '')
                return true;

            const items = this.lookupitems || this.titleMap;
            if (!Array.isArray(items) || items.length === 0)
                return false;

            return items.some(a => value == this.getItemValue(a));
        },
        async Refresh() {
            if (this.tolookup) {
                if (this.lookupurlexpn) {
                    let url = this.lookupurl;
                    if (url)
                        try {
                            this.lookupitems = await utils.api.get(url, false, this.formatData.CacheLookupResult);
                        }
                        catch (e) {
                            utils.warn(`Failed in form select lookupurl:${url}`, e);
                            this.lookupitems = [];
                        }
                }
                else if (this.lookuprawexpn) {
                    this.lookupitems = this.lookupraw;
                }
            }

            const items = this.lookupitems || this.titleMap;

            const allowCustom = this.formatData && this.formatData.AllowCustomInput;
            const defaultAsEmpty = this.formatData && this.formatData.DefaultAsEmpty;

            const allowsNull = this.element && this.element.schema && this.element.schema.$allows_null ? true : false;

            if (!allowCustom && Array.isArray(items) && items.length > 0) {
                const currentValue = this.itemvalue;
                const hasDefinedValue = currentValue !== undefined && currentValue !== null && currentValue !== '';

                if (hasDefinedValue && !this.valueExistsInOptions(currentValue)) {
                    // Preserve Advanced expressions; Essentials shows an expression chip instead.
                    if (this.isEssentialsExpressionMode)
                        return;

                    this.setValue(null);

                    if (this.arrayfield && this.arrayelement)
                        this.arrayfield.availableitems[this.chosenitems_key] = items && (this.arrayelement.length < items.length);

                    return;
                }
            }

            // If no item is already selected, auto-select the first unused item in the list
            if (!allowCustom && !allowsNull && !defaultAsEmpty && !this.isEssentialsExpressionMode && Array.isArray(items) && items.length > 0) {
                let idx = items.findIndex(a => this.itemvalue == this.getItemValue(a));
                if (idx < 0) {
                    if (this.arrayfield && this.arrayfield.chosenitems && this.arrayfield.chosenitems[this.chosenitems_key])
                        idx = items.findIndex(a => !(this.getItemValue(a) in this.arrayfield.chosenitems[this.chosenitems_key]));
                    else
                        idx = 0;

                    if (idx >= 0)
                        this.setValue(this.getItemValue(items[idx]));
                }

                if (this.arrayfield && this.arrayelement)
                    // Update the array container to control if the Add button is enabled or not
                    this.arrayfield.availableitems[this.chosenitems_key] = items && (this.arrayelement.length < items.length);
            }
        },
        emptyItemLabel() {
            return this.formatData?.EmptyItemLabel || this.element?.formatData?.EmptyItemLabel || '';
        },
        buildEmptyLookupRow() {
            const fd = this.formatData || {};
            const displayField = fd.DisplayField || 'name';
            const valueField = fd.ValueField || 'value';
            const row = { $emptyrow: true };
            if (displayField !== valueField) {
                row[displayField] = this.emptyItemLabel();
                row[valueField] = null;
            }
            return row;
        },
        /** Match Vuetify 2 v-select: getValue falls back to getText when item-value path is undefined. */
        getLookupItemTextValue(item) {
            if (typeof this.item_text === 'function')
                return this.item_text(item);
            if (typeof this.item_text === 'string')
                return this.getByPath(item, this.item_text);
            return undefined;
        },
        getItemValue(item) {
            if (item && item.$emptyrow)
                return null;
            let value;
            if (typeof this.item_value === 'function')
                value = this.item_value(item);
            else if (typeof this.item_value === 'string')
                value = this.getByPath(item, this.item_value);
            else
                return null;
            // Empty/missing ValueField (common on LookupList selects) yields undefined; Vue2
            // Vuetify falls back to the display text, so Quasar emit-value must do the same.
            if (value === undefined)
                return this.getLookupItemTextValue(item);
            return value;
        },
        setValue(value) {
            this.sync(value);

            if (this.arrayfield && this.arrayelement) {
                // Uniqueness has been requested and we have a reference to the array of items already picked.
                // We need to generate a lookup object containing every selected item so that we can disable
                // those that are already picked in the current dropdown list.
                this.arrayfield.chosenitems[this.chosenitems_key] = Object.fromEntries(this.arrayelement.map(a => [this.keyextractor(a), true]));

                // Update the array container to control if the Add button is enabled or not
                this.arrayfield.availableitems[this.chosenitems_key] = (this.lookupitems && this.arrayelement.length < this.lookupitems.length) || (this.titleMap && this.arrayelement.length < this.titleMap.length);
            }
        },
        resolveDisplayText(value) {
            if (value === null || value === undefined)
                return '';
            const match = (this.listitems || []).find(a => a._value == value);
            if (match)
                return match._label ?? String(value);
            return String(value);
        },
        resolveCommitValue(text) {
            const match = (this.listitems || []).find(a => a._label == text || a._value == text);
            return match ? match._value : text;
        },
        applyCustomInputEdit(val, { fromFilter = false } = {}) {
            if (!this.formatData?.AllowCustomInput || !this.isCustomInputEditing)
                return;
            const next = val ?? '';
            const display = this.resolveDisplayText(this.itemvalue);

            // Quasar focus often @filter's with "" or echoes the current label. Those are not edits.
            // Empty from inputValue/keyup is treated as an intentional clear.
            if (!this.customInputDirty && next === display)
                return;
            if (!this.customInputDirty && next === '' && fromFilter)
                return;

            // Once the user has typed, don't let fill-input/map-options sync overwrite the typed
            // text with the previous selection's label before blur commits.
            if (this.customInputDirty && next === display && this.customInputText !== display)
                return;

            this.customInputDirty = true;
            this.customInputText = next;
        },
        filterFn(val, update) {
            // Quasar use-input delivers typed text primarily through @filter. Capture edits here,
            // but ignore focus resets so we don't commit "" and wipe the selection on blur.
            update(() => {
                this.filter = val;
                this.applyCustomInputEdit(val, { fromFilter: true });
            });
        },
        onCustomInputValue(val) {
            this.applyCustomInputEdit(val);
        },
        onCustomInputFocus() {
            this.onFocus();
            if (this.formatData?.AllowCustomInput) {
                this.isCustomInputEditing = true;
                this.customInputText = this.resolveDisplayText(this.itemvalue);
                this.customInputDirty = false;
            }
        },
        commitCustomInput() {
            if (!this.formatData?.AllowCustomInput || !this.isCustomInputEditing)
                return;
            const dirty = this.customInputDirty;
            const text = this.customInputText ?? '';
            this.isCustomInputEditing = false;
            this.customInputDirty = false;
            this.customInputText = '';
            this.filter = '';
            if (dirty)
                this.setValue(this.resolveCommitValue(text));
        },
        onCustomInputBlur() {
            this.commitCustomInput();
            this.onBlur();
        },
        onCustomInputKeydown(e) {
            if (!this.formatData?.AllowCustomInput || !this.isCustomInputEditing)
                return;
            if (e.key === 'Enter' || e.key === 'Tab')
                this.commitCustomInput();
        },
        onCustomInputKeyup(e) {
            // Backup path when Quasar shows typed text visually but inputValue updates are sparse.
            if (!this.formatData?.AllowCustomInput || !this.isCustomInputEditing)
                return;
            this.applyCustomInputEdit(e?.target?.value ?? '');
        },
        onModelValueUpdate(value) {
            this.isCustomInputEditing = false;
            this.customInputDirty = false;
            this.customInputText = '';
            this.filter = '';
            this.setValue(value);
        },
    },
    render() {
        const variableField = this.tryRenderVariableBindingField({
            class: 'c-formSelect caption c-variable-binding-select',
            labelSlot: true,
        });
        if (variableField)
            return variableField;

        //const items = this.listitems; // this.lookupitems || this.titleMap || [];

        const allowCustom = !!this.formatData?.AllowCustomInput;
        const useInput = !!(allowCustom || this.formatData?.AutoCompleteInput);

        // map-options stays on for custom-input fields as well. It used to be switched off for them,
        // which meant the input rendered the stored value while the dropdown beside it rendered the
        // label, so any option whose value and label differ disagreed with itself on screen.
        // selectListItems keeps the current value present in the option list so it always maps.

        let slots = {};
        if (this.hasInputLabel)
            slots.label = () => <TranslationContainer context={this} value={this.labelText} autoOpenModal></TranslationContainer>;

        if (this.hintText)
            slots.hint = () => <TranslationContainer context={this} value={this.hintText} autoOpenModal></TranslationContainer>;

        slots = this.mergeVariableAssistSlots(slots);

        let input = (
            <QSelect
                ref="form"
                rules={this.rules} {...this.compilerErrorFieldProps}
                class="c-formSelect caption"
                bg-color="white"
                outlined
                dense
                labelSlot={this.hasInputLabel}
                bottom-slots={this.showFieldBottomSlots}
                hide-hint
                popup-content-class="q-select-max-popup-height"
                style={{width: "100%"}}
                modelValue={this.displayModelValue}
                options={this.selectListItems}
                option-label="_label"
                option-value="_value"
                option-disable={this.item_disabled}
                emit-value
                map-options
                use-input={useInput}
                fillInput={useInput}
                hideSelected={useInput}
                inputDebounce={0}
                newValueMode={allowCustom ? 'add-unique' : undefined}
                inputValue={allowCustom && this.isCustomInputEditing ? this.customInputText : undefined}
                onUpdate:inputValue={allowCustom ? this.onCustomInputValue : undefined}
                onFilter={useInput ? this.filterFn : undefined}
                onUpdate:modelValue={this.onModelValueUpdate}
                onFocus={allowCustom ? () => { this._vbFocused = true; this.onCustomInputFocus(); } : () => { this._vbFocused = true; this.onFocus(); }}
                onBlur={allowCustom ? () => { this._vbFocused = false; this.onCustomInputBlur(); } : () => { this._vbFocused = false; this.onBlur(); }}
                onKeydown={allowCustom ? this.onCustomInputKeydown : undefined}
                onKeyup={allowCustom ? this.onCustomInputKeyup : undefined}
                autocomplete="off"
            >
                {slots}
            </QSelect>
        );

        if (this.appearance)
            utils.applyVue2ToVue3Props(input, this.appearance);
        //input.componentOptions.propsData = { ...input.componentOptions.propsData, ...this.appearance };

        // if (this.directives && this.directives.some(a => a.name == 'on-enter-default'))
        //    input.props.autofocus = true;

        if (this.directives) {
            var applyDirectives = this.directives.map(d => {
                const dirDef = d.directive || resolveDirective(d.name);

                return [dirDef, d.value, d.arg, d.modifiers];
            });
            input = withDirectives(input, applyDirectives);
        }

        return this.wrapLiteralWithVariableAssist(input);
    }
});